import {
  Box,
  ErrorModal,
  Flex,
  Heading,
  Text,
  TextButton,
  useOpenModal,
} from '@modusign/design-system';
import moment from 'moment';
import { useState } from 'react';
import { Prompt, useHistory } from 'react-router';

import { createUseResponsiveValue } from '@hooks';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import LocalHeader from '@app/components/LocalHeader';
import { LabelListSummary } from '@app/features/label';
import LabelListModal from '@app/features/label/components/LabelListModal';
import usePreventUnload from '@app/hooks/usePreventUnload';

import useExternalDocument from '../../hooks/useExternalDocument';
import ExternalDocumentListItemMenuButton from '../ExternalDocumentListItemMenuButton';
import BasicInformationPanel from './BasicInformationPanel';
import FileList from './FileList';
import ParticipantsPanel from './ParticipantsPanel';
import Tab from './Tab';

const preventUnloadMessage = '지금 이 페이지를 벗어날 경우, 진행 중인 작업이 중단됩니다.';

interface DocumentDetailPageProps {
  documentId: string;
}

const SHOW_FILE_LIST_VIEWPORT = 900;
const useFileListHide = createUseResponsiveValue([SHOW_FILE_LIST_VIEWPORT]);

export default function DocumentDetailPage({ documentId }: DocumentDetailPageProps) {
  const openModal = useOpenModal();
  const history = useHistory();

  const { data, isLoading } = useExternalDocument(documentId, {
    onError: (error) => {
      history.push('/external-documents/list');
      openModal((props) => <ErrorModal error={error} title="계약 불러오기 실패" {...props} />, {
        keepOnUnmount: true,
      });
    },
    retry: false,
  });

  const showFileListOnTheAside = useFileListHide([false, true]);

  const [tab, setTab] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  usePreventUnload(isDirty);

  if (isLoading) {
    return <CenterLoadingSpinner />;
  }

  if (!data) {
    return null;
  }

  const files = [
    { id: data.file.id, name: data.file.name, isContract: true, extension: data.file.extension },
    ...data.attachments.map(({ id, name, extension }) => ({
      id,
      name,
      isContract: false,
      extension,
    })),
  ];

  return (
    <Flex
      flexDirection="column"
      sx={{
        minHeight: '100vh',
        pl: [0, null, '200px'],
        width: '100%',
        pb: [0, null, '120px'],
        pt: ['60px', null, 0],
      }}
    >
      <Prompt message={preventUnloadMessage} when={isDirty} />
      <LocalHeader isHelpBtn>
        <Flex alignItems={['flex-start', 'center']} flexDirection={['column', 'row']} gap={3}>
          <Heading fontSize={['md', null, 'xl']} fontWeight="medium" lineHeight={1}>
            외부문서함
          </Heading>
        </Flex>
      </LocalHeader>
      <Flex flex="auto" flexDirection="column">
        <Flex flex="auto">
          <Flex flex="auto" flexDirection="column" px={[4, null, 9]}>
            <Box pb={[7, null, 9]} pt={[4, null, 7]}>
              <header>
                <TextButton sx={{ textDecoration: 'none' }} onClick={history.goBack}>
                  {'<'} 목록으로
                </TextButton>
                <Flex
                  alignItems="stretch"
                  flexDirection={['column', null, 'row']}
                  justifyContent="space-between"
                  mt={7}
                >
                  <Box flex="auto">
                    <Heading as="h2" fontSize="xl" fontWeight="bold">
                      {data?.title}
                    </Heading>
                    <Flex flexDirection="column" gap={1} mt={5}>
                      <Flex>
                        <Text color="textNeutralLight" mr={4} sx={{ flex: '0 0 100px' }}>
                          마지막 활동 시각
                        </Text>
                        <Text>{moment(data.updatedAt).format('YYYY.MM.DD A hh:mm')}</Text>
                      </Flex>
                      <Flex>
                        <Text color="textNeutralLight" mr={4} sx={{ flex: '0 0 100px' }}>
                          라벨
                        </Text>
                        <LabelListSummary
                          labelIds={data.labels.map((l) => l.id)}
                          onClick={() => {
                            openModal(({ isOpen, onClose }) => (
                              <LabelListModal
                                isOpen={isOpen}
                                resourceId={data.id}
                                resourceType="EXTERNAL_DOCUMENT"
                                onClose={onClose}
                              />
                            ));
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Box>
                  <Flex flexShrink={0} height="36px" justifyContent="flex-end" mt={[6, null, 0]}>
                    <ExternalDocumentListItemMenuButton
                      context="DETAIL_PAGE"
                      externalDocument={{ id: documentId, title: data.title }}
                    />
                  </Flex>
                </Flex>
                {!showFileListOnTheAside && (
                  <Flex
                    borderColor="dividerNeutralLight"
                    borderStyle="solid"
                    borderWidth="1px 0"
                    flexDirection="column"
                    mt={[7, null, 9]}
                    py={7}
                  >
                    <FileList
                      documentId={documentId}
                      files={files}
                      isCompact
                      onClick={({ id, isContract }) =>
                        history.push(
                          `/external-documents/file-viewer/${documentId}?${
                            isContract ? 'fileId' : 'attachmentId'
                          }=${id}`
                        )
                      }
                    />
                  </Flex>
                )}
              </header>
            </Box>
            <Tab
              selectedTabIndex={tab}
              tabs={['계약 정보', '서명자 정보']}
              onTabChange={(tab) => {
                // eslint-disable-next-line no-alert
                if (!isDirty || window.confirm(preventUnloadMessage)) {
                  setTab(tab);
                  setIsDirty(false);
                }
              }}
            />
            {tab === 0 && (
              <BasicInformationPanel externalDocument={data} onIsDirtyChange={setIsDirty} />
            )}
            {tab === 1 && (
              <ParticipantsPanel externalDocument={data} onIsDirtyChange={setIsDirty} />
            )}
          </Flex>
          {showFileListOnTheAside && (
            <Flex
              as="aside"
              borderLeft="1px solid"
              borderLeftColor="lightGray300"
              flex="none"
              flexDirection="column"
              minWidth="280px"
              p={7}
              width="20vw"
            >
              <FileList
                documentId={documentId}
                files={files}
                onClick={({ id, isContract }) =>
                  history.push(
                    `/external-documents/file-viewer/${documentId}?${
                      isContract ? 'fileId' : 'attachmentId'
                    }=${id}`
                  )
                }
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
